// SessionProvider: bootstraps the MedReview session via one of three paths:
//
// 1. Shopify App Bridge JWT exchange when running inside the Shopify
//    admin embed (window.shopify.idToken() returns a token).
// 2. Magic-link token consume when the URL has ?token=... in the query
//    string (deep-linked from the sign-in email).
// 3. Manual sign-in via LoginPage when neither of the above applies;
//    setSessionToken is exposed so the login UI can hand off after a
//    successful /api/auth/magic-link-consume call.
//
// The session JWT is stored in React state only (no localStorage; PHI
// hygiene per CLAUDE.md). 401 from any /api call triggers refresh()
// which re-runs the appropriate path.

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { env } from '../env';
import { useSessionToken } from './useSessionToken';

interface SessionState {
  sessionToken: string | null;
  /**
   * 'idle'         haven't tried yet
   * 'exchanging'   in-flight Shopify or magic-link exchange
   * 'ready'        sessionToken set, ready for API calls
   * 'no-shopify'   not in Shopify embed, no URL token; show LoginPage
   * 'error'        last exchange failed; errorMessage explains
   */
  status: 'idle' | 'exchanging' | 'ready' | 'no-shopify' | 'error';
  errorMessage?: string;
}

interface SessionContextValue extends SessionState {
  /** Re-run Shopify exchange (only useful when embedded). */
  refresh: () => Promise<string | null>;
  /** Set the session token after a successful magic-link consume. */
  setSessionToken: (token: string) => void;
  /** Clear the session token (sign out). */
  clear: () => void;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

const SHOPIFY_EXCHANGE_PATH = '/api/auth/shopify-exchange';
const MAGIC_LINK_CONSUME_PATH = '/api/auth/magic-link-consume';

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const getShopifyToken = useSessionToken();
  const [state, setState] = useState<SessionState>({
    sessionToken: null,
    status: 'idle',
  });
  const inflight = useRef<Promise<string | null> | null>(null);

  const consumeUrlToken = useCallback(async (): Promise<string | null> => {
    if (typeof window === 'undefined') return null;
    const url = new URL(window.location.href);
    const token = url.searchParams.get('token');
    if (!token) return null;
    setState((s) => ({ ...s, status: 'exchanging' }));
    try {
      const res = await fetch(`${env.apiBaseUrl}${MAGIC_LINK_CONSUME_PATH}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) {
        const text = await res.text();
        setState({
          sessionToken: null,
          status: 'error',
          errorMessage: `Sign-in link invalid or expired: HTTP ${res.status} ${text.slice(0, 100)}`,
        });
        return null;
      }
      const json = (await res.json()) as {
        ok: boolean;
        data?: { sessionToken: string };
        error?: { code: string; message: string };
      };
      if (!json.ok || !json.data?.sessionToken) {
        setState({
          sessionToken: null,
          status: 'error',
          errorMessage: json.error?.message ?? 'Sign-in failed',
        });
        return null;
      }
      // Strip the token from the URL so refresh doesn't try to re-consume,
      // and send the user to the app root since the magic-link path
      // (/auth/magic) has no route. replaceState alone updates the URL
      // bar but does NOT notify React Router; dispatching popstate makes
      // BrowserRouter re-read window.location and re-resolve routes.
      url.searchParams.delete('token');
      url.pathname = '/';
      window.history.replaceState({}, '', url.toString());
      window.dispatchEvent(new PopStateEvent('popstate'));
      const sessionToken = json.data.sessionToken;
      setState({ sessionToken, status: 'ready' });
      return sessionToken;
    } catch (err) {
      setState({
        sessionToken: null,
        status: 'error',
        errorMessage: err instanceof Error ? err.message : 'Sign-in threw',
      });
      return null;
    }
  }, []);

  const exchange = useCallback(async (): Promise<string | null> => {
    if (inflight.current) return inflight.current;
    const run = (async (): Promise<string | null> => {
      // E2E shortcut: Playwright tests inject a pre-issued session
      // JWT on window.__MEDREVIEW_TEST_TOKEN__ before the SPA boots.
      // Skip Shopify exchange entirely when present.
      const testToken =
        typeof window !== 'undefined'
          ? (window as unknown as { __MEDREVIEW_TEST_TOKEN__?: string }).__MEDREVIEW_TEST_TOKEN__
          : undefined;
      if (testToken) {
        setState({ sessionToken: testToken, status: 'ready' });
        return testToken;
      }

      // Magic-link wins if a ?token= is present in the URL.
      const urlConsumed = await consumeUrlToken();
      if (urlConsumed) return urlConsumed;

      setState((s) => ({ ...s, status: 'exchanging' }));
      const shopifyToken = await getShopifyToken();
      if (!shopifyToken) {
        setState({ sessionToken: null, status: 'no-shopify' });
        return null;
      }
      try {
        const res = await fetch(`${env.apiBaseUrl}${SHOPIFY_EXCHANGE_PATH}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionToken: shopifyToken }),
        });
        if (!res.ok) {
          const text = await res.text();
          setState({
            sessionToken: null,
            status: 'error',
            errorMessage: `Exchange failed: HTTP ${res.status} ${text.slice(0, 120)}`,
          });
          return null;
        }
        const json = (await res.json()) as {
          ok: boolean;
          data?: { sessionToken: string };
          error?: { code: string; message: string };
        };
        if (!json.ok || !json.data?.sessionToken) {
          setState({
            sessionToken: null,
            status: 'error',
            errorMessage: json.error?.message ?? 'Exchange returned no token',
          });
          return null;
        }
        const token = json.data.sessionToken;
        setState({ sessionToken: token, status: 'ready' });
        return token;
      } catch (err) {
        setState({
          sessionToken: null,
          status: 'error',
          errorMessage: err instanceof Error ? err.message : 'Exchange threw',
        });
        return null;
      }
    })();
    inflight.current = run;
    try {
      return await run;
    } finally {
      inflight.current = null;
    }
  }, [getShopifyToken, consumeUrlToken]);

  useEffect(() => {
    void exchange();
  }, [exchange]);

  const setSessionToken = useCallback((token: string) => {
    setState({ sessionToken: token, status: 'ready' });
  }, []);

  const clear = useCallback(() => {
    setState({ sessionToken: null, status: 'no-shopify' });
  }, []);

  return (
    <SessionContext.Provider value={{ ...state, refresh: exchange, setSessionToken, clear }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession must be used within <SessionProvider>');
  }
  return ctx;
}
